import React from 'react';
import { DEFAULT_CHECKOUT_LABELS, subtotalLabel, type CheckoutLabels } from './checkoutLabels';
import type { OfferSaving } from '../commerce/offerSavings';

interface CheckoutTotalsProps {
  quantity: number;
  /** What the lines cost after the store's quantity offers; the subtotal adds the offers back to show full price. */
  merchandiseTotal: number;
  /** Each quantity offer's saving, listed by name under the subtotal as Shopify lists a discount. */
  offers?: OfferSaving[];
  bump: { label: string; amount: number } | null;
  /** The applied discount code and what it takes off, shown as a minus line above the total. */
  discount?: { label: string; amount: number } | null;
  shippingFee: number | null;
  total: number | null;
  format: (value: number) => string;
  // A theme in another language overrides these; the default keeps the unthemed storefront exactly as it was.
  labels?: CheckoutLabels;
}

// The money the order will be created with: every number here was priced by the server, never by this page.
export const CheckoutTotals: React.FC<CheckoutTotalsProps> = ({
  quantity, merchandiseTotal, offers = [], bump, discount, shippingFee, total, format, labels = DEFAULT_CHECKOUT_LABELS
}) => (
  <div className="totals-summary">
    <div className="total-row">
      <span>{subtotalLabel(labels, quantity)}</span>
      <span>{format(merchandiseTotal + offers.reduce((sum, offer) => sum + offer.amount, 0))}</span>
    </div>

    {offers.map((offer) => (
      <div key={offer.label} className="total-row total-row-saving" style={{ color: 'var(--xe-success, #047857)' }}>
        <span dir="auto">{offer.label}</span>
        <span>−{format(offer.amount)}</span>
      </div>
    ))}

    {bump && (
      <div className="total-row">
        <span dir="auto">{bump.label} :</span>
        <span>{format(bump.amount)}</span>
      </div>
    )}

    {discount && discount.amount > 0 && (
      <div className="total-row" style={{ color: 'var(--xe-success, #047857)' }}>
        <span dir="auto">{discount.label}</span>
        <span>−{format(discount.amount)}</span>
      </div>
    )}

    <div className="total-row">
      <span>{labels.shipping}</span>
      <span>
        {shippingFee === null && <span className="total-pending">{labels.shippingPending}</span>}
        {shippingFee === 0 && <strong style={{ color: 'var(--xe-success, #047857)' }}>{labels.shippingFree}</strong>}
        {shippingFee !== null && shippingFee > 0 && format(shippingFee)}
      </span>
    </div>

    <div className="total-row final">
      <span>{labels.total}</span>
      <span className="total-price-green">{total === null ? '—' : format(total)}</span>
    </div>
  </div>
);
